import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import {
  ShieldCheck, MessageSquare, Facebook, Instagram, Check, X, FileText, Activity,
  Building2, AppWindow, UserCheck, AlertTriangle
} from "lucide-react";

const Dot = ({ ok }) => ok
  ? <Check className="h-4 w-4 text-emerald-600" />
  : <X className="h-4 w-4 text-rose-500" />;

const Card = ({ title, icon: Icon, children, testid }) => (
  <div className="rounded-2xl border border-border bg-card p-5" data-testid={testid}>
    <div className="flex items-center gap-2 mb-4">
      <div className="h-8 w-8 rounded-lg bg-accent text-primary flex items-center justify-center"><Icon className="h-4 w-4" /></div>
      <h3 className="font-display font-semibold text-sm uppercase tracking-wide">{title}</h3>
    </div>
    <div className="space-y-2.5 text-sm">{children}</div>
  </div>
);

const Line = ({ label, ok, value }) => (
  <div className="flex items-center justify-between">
    <span className="text-muted-foreground">{label}</span>
    {value !== undefined ? <span className="font-medium">{value}</span> : <Dot ok={ok} />}
  </div>
);

export default function MetaCenter() {
  const { data } = useQuery({ queryKey: ["meta-center"], queryFn: () => api.get("/meta-center").then((r) => r.data) });
  if (!data) return <div className="p-6"><div className="h-8 w-40 bg-secondary rounded animate-pulse" /></div>;

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto" data-testid="meta-center-page">
      <div className="flex items-center gap-4">
        <img src="/meta-approved.png" alt="Meta Approved" data-testid="meta-approved-logo" className="h-16 w-16 object-contain shrink-0" />
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2"><ShieldCheck className="h-6 w-6 text-primary" /> Meta Center</h1>
          <p className="text-muted-foreground text-sm mt-1">Status sambungan, compliance & kualiti akaun WhatsApp Business anda.</p>
        </div>
      </div>

      {!data.app.access_approved && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900/40 p-4 text-sm text-amber-800 dark:text-amber-300 flex gap-3">
          <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
          <p><span className="font-semibold">Mod Demo — belum LIVE.</span> Untuk go-live: (1) Meta Business Verification, (2) Meta App + WhatsApp product, (3) App Review + video use-case, (4) Advanced Access untuk <span className="font-mono text-xs">whatsapp_business_management</span>, <span className="font-mono text-xs">whatsapp_business_messaging</span>, <span className="font-mono text-xs">business_management</span>. Ini langkah manual di Meta yang tidak boleh diautomasikan.</p>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        <Card title="Business" icon={Building2} testid="meta-card-business">
          <Line label="Business Connected" ok={data.business.connected} />
          <Line label="Nama" value={data.business.name} />
        </Card>

        <Card title="WhatsApp" icon={MessageSquare} testid="meta-card-whatsapp">
          <Line label="WABA Connected" ok={data.whatsapp.waba_connected} />
          <Line label="Phone Connected" ok={data.whatsapp.phone_connected} />
          <Line label="Webhook Active" ok={data.whatsapp.webhook_active} />
          {data.whatsapp.numbers.map((n) => <div key={n} className="text-xs text-muted-foreground pl-1">• {n}</div>)}
        </Card>

        <Card title="App" icon={AppWindow} testid="meta-card-app">
          <Line label="App Live" ok={data.app.live} />
          <Line label="Access Approved" ok={data.app.access_approved} />
          <Line label="Mode" value={<Badge variant="outline" className="rounded-full text-[10px]">{data.app.mode}</Badge>} />
        </Card>

        <Card title="Facebook & Instagram" icon={Facebook} testid="meta-card-social">
          <Line label="Facebook Page" ok={data.facebook.connected} />
          {data.facebook.pages.map((p) => <div key={p} className="text-xs text-muted-foreground pl-1">• {p}</div>)}
          <Line label="Instagram Business" ok={data.instagram.connected} />
          {data.instagram.accounts.map((a) => <div key={a} className="text-xs text-muted-foreground pl-1">• {a}</div>)}
        </Card>

        <Card title="Compliance" icon={UserCheck} testid="meta-card-compliance">
          <Line label="Privacy Policy" ok={data.compliance.privacy_policy} />
          <Line label="Terms of Service" ok={data.compliance.terms} />
          <Line label="Data Deletion" ok={data.compliance.data_deletion} />
          <Line label="Consent Tracking" ok={data.compliance.consent_tracking} />
          <Line label="Opt-out Engine" ok={data.compliance.optout_engine} />
          <Line label="Human Escalation" ok={data.compliance.human_escalation} />
        </Card>

        <Card title="Templates" icon={FileText} testid="meta-card-templates">
          <Line label="Approved" value={<span className="text-emerald-600 font-semibold">{data.templates.approved}</span>} />
          <Line label="Pending" value={<span className="text-amber-600 font-semibold">{data.templates.pending}</span>} />
          <Line label="Rejected" value={<span className="text-rose-600 font-semibold">{data.templates.rejected}</span>} />
        </Card>

        <Card title="Consent" icon={UserCheck} testid="meta-card-consent">
          <Line label="Opted-in" value={<span className="text-emerald-600 font-semibold">{data.consent.opted_in}</span>} />
          <Line label="Opted-out" value={<span className="text-rose-600 font-semibold">{data.consent.opted_out}</span>} />
          <Line label="Jumlah" value={data.consent.total} />
        </Card>

        <Card title="Quality" icon={Activity} testid="meta-card-quality">
          <Line label="Phone Quality" value={<Badge className="bg-emerald-100 text-emerald-700 border-0 rounded-full">{data.quality.phone_quality}</Badge>} />
          <Line label="Webhook Health" value={<span className="font-semibold">{data.quality.webhook_health}</span>} />
        </Card>
      </div>
    </div>
  );
}
